import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown, FaSearch, FaTimes } from "react-icons/fa";
import { usePortalPosition } from "./usePortalPosition";

export const SEARCH_SELECT_INPUT_CLASS =
  "w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]";

const triggerResetStyle = {
  boxShadow: "none",
  WebkitAppearance: "none",
  appearance: "none",
};

function SearchSelectDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  loading = false,
  loadingLabel = "Loading...",
  emptyLabel = "No options available",
  inputClass = SEARCH_SELECT_INPUT_CLASS,
  usePortal = false,
  allowDeselect = false,
  clearable = false,
  menuClassName = "z-[60]",
  openOnHover = false,
  hoverBoundaryRef = null,
  hideSelectionInTrigger = false,
}) {
  const hoverOpen = openOnHover;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const hoverCloseRef = useRef(null);

  const portalStyle = usePortalPosition(triggerRef, open && usePortal, { placement: "bottom", gap: 4 });

  const normalizedOptions = useMemo(
    () =>
      options.map((option) =>
        typeof option === "string" ? { value: option, label: option } : option,
      ),
    [options],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inContainer = containerRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inContainer && !inMenu) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      if (!hoverOpen) {
        setTimeout(() => searchRef.current?.focus(), 0);
      }
    } else {
      setSearch("");
    }
  }, [open, hoverOpen]);

  useEffect(
    () => () => {
      if (hoverCloseRef.current) {
        clearTimeout(hoverCloseRef.current);
      }
    },
    [],
  );

  const cancelHoverClose = () => {
    if (hoverCloseRef.current) {
      clearTimeout(hoverCloseRef.current);
      hoverCloseRef.current = null;
    }
  };

  const isPointerOverDropdown = () => {
    if (containerRef.current?.matches(":hover")) return true;
    if (menuRef.current?.matches(":hover")) return true;
    if (hoverBoundaryRef?.current?.matches(":hover")) return true;
    return false;
  };

  const scheduleHoverClose = () => {
    if (!hoverOpen) return;
    cancelHoverClose();
    hoverCloseRef.current = setTimeout(() => {
      if (!isPointerOverDropdown()) {
        setOpen(false);
        setSearch("");
      }
    }, 220);
  };

  useEffect(() => {
    const boundary = hoverBoundaryRef?.current;
    if (!boundary || !openOnHover) return undefined;

    const handleBoundaryEnter = () => {
      if (disabled || loading) return;
      cancelHoverClose();
      setOpen(true);
    };

    const handleBoundaryLeave = () => {
      scheduleHoverClose();
    };

    boundary.addEventListener("mouseenter", handleBoundaryEnter);
    boundary.addEventListener("mouseleave", handleBoundaryLeave);
    return () => {
      boundary.removeEventListener("mouseenter", handleBoundaryEnter);
      boundary.removeEventListener("mouseleave", handleBoundaryLeave);
    };
  }, [hoverBoundaryRef, openOnHover, disabled, loading]);

  const handleContainerMouseEnter = () => {
    if (!hoverOpen || disabled || loading) return;
    cancelHoverClose();
    setOpen(true);
  };

  const handleContainerMouseLeave = () => {
    if (!hoverOpen) return;
    scheduleHoverClose();
  };

  const selectedOption = useMemo(
    () => normalizedOptions.find((option) => option.value === value) || null,
    [normalizedOptions, value],
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return normalizedOptions;
    return normalizedOptions.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [normalizedOptions, search]);

  const handleSelect = (optionValue) => {
    if (allowDeselect && value === optionValue) {
      onChange("");
    } else {
      onChange(optionValue);
    }
    setOpen(false);
    setSearch("");
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange("");
    setOpen(false);
    setSearch("");
  };

  const renderMenu = () => (
    <div
      ref={menuRef}
      onMouseEnter={hoverOpen ? cancelHoverClose : undefined}
      onMouseLeave={hoverOpen ? scheduleHoverClose : undefined}
      className={`overflow-hidden rounded-lg bg-white ${
        usePortal ? menuClassName : `absolute mt-1 w-full ${menuClassName}`
      }`}
      style={
        usePortal
          ? {
              ...portalStyle,
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }
          : {
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }
      }
    >
      <div
        className="p-2"
        style={{ borderBottom: "1px solid #f3f4f6", backgroundColor: "#fafbfc" }}
      >
        <div className="relative">
          <FaSearch
            className="pointer-events-none text-gray-400"
            size={11}
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-md border border-gray-300 bg-white py-1.5 text-xs text-gray-900 focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
            style={{ paddingLeft: "30px", paddingRight: "8px", boxShadow: "none" }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>

      <ul
        className="max-h-44 overflow-y-auto"
        style={{ listStyle: "none", margin: 0, padding: "4px 0" }}
      >
        {filteredOptions.length === 0 ? (
          <li className="px-3 py-2 text-center text-xs text-gray-400">
            {search.trim() ? "No results found" : emptyLabel}
          </li>
        ) : (
          filteredOptions.map((option) => (
            <li key={option.value} style={{ margin: 0, padding: 0 }}>
              <button
                type="button"
                onClick={() => handleSelect(option.value)}
                className="w-full text-left text-xs transition"
                style={{
                  border: "none",
                  outline: "none",
                  background: value === option.value ? "rgba(0, 123, 255, 0.08)" : "transparent",
                  color: value === option.value ? "#007BFF" : "#374151",
                  fontWeight: value === option.value ? 600 : 400,
                  padding: "8px 12px",
                  cursor: "pointer",
                  boxShadow: "none",
                }}
                onMouseEnter={(e) => {
                  if (value !== option.value) {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                  }
                }}
                onMouseLeave={(e) => {
                  if (value !== option.value) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                {option.label}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );

  const handleTriggerClick = () => {
    if (disabled || loading) return;
    if (hoverOpen) {
      setOpen(true);
      return;
    }
    setOpen((prev) => !prev);
  };

  const showSelectedInTrigger = selectedOption && !hideSelectionInTrigger;
  const triggerLabel = loading
    ? loadingLabel
    : showSelectedInTrigger
      ? selectedOption.label
      : placeholder;
  const showClearInTrigger =
    clearable && selectedOption && !hideSelectionInTrigger && !loading && !disabled;

  return (
    <div
      className="relative"
      ref={containerRef}
      onMouseEnter={handleContainerMouseEnter}
      onMouseLeave={handleContainerMouseLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        onClick={handleTriggerClick}
        className={`${inputClass} flex items-center justify-between text-left ${
          disabled || loading ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        }`}
        style={triggerResetStyle}
      >
        <span className={`truncate ${showSelectedInTrigger ? "text-gray-900" : "text-gray-500"}`}>
          {triggerLabel}
        </span>
        <span className="ml-2 flex shrink-0 items-center gap-1">
          {showClearInTrigger ? (
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              style={{ border: "none", background: "transparent", padding: 0 }}
              aria-label="Clear selection"
            >
              <FaTimes size={9} />
            </button>
          ) : null}
          <FaChevronDown
            size={10}
            className={`text-gray-400 transition ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {open &&
        !disabled &&
        !loading &&
        (usePortal ? createPortal(renderMenu(), document.body) : renderMenu())}
    </div>
  );
}

export default SearchSelectDropdown;
